import React, { use, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import styles from "./Sidebar.module.scss";
import logo from "../../assets/images/main-logo.png";
import logosmall from "../../assets/images/logo-small.png";

import { ReactComponent as DashboardIcon } from "../../assets/icons/dashboard-icon.svg";
import { ReactComponent as DashboardIconHover } from "../../assets/icons/dashboard-icon-h.svg";
import { ReactComponent as DashboardIconActive } from "../../assets/icons/dashboard-icon-act.svg";
import { ReactComponent as ApplicationIcon } from "../../assets/icons/application-icon.svg";
import { ReactComponent as ApplicationIconHover } from "../../assets/icons/application-icon-h.svg";
import { ReactComponent as ApplicationIconActive } from "../../assets/icons/application-icon-act.svg";
import { ReactComponent as ScholarshipIcon } from "../../assets/icons/scolarship-icon.svg";
import { ReactComponent as ScholarshipIconHover } from "../../assets/icons/scolarship-icon-h.svg";
import { ReactComponent as ScholarshipIconActive } from "../../assets/icons/scolarship-icon-act.svg";
import { ReactComponent as FundMngIcon } from "../../assets/icons/fundmng-icon.svg";
import { ReactComponent as FundMngIconHover } from "../../assets/icons/fundmng-icon-h.svg";
import { ReactComponent as FundMngIconActive } from "../../assets/icons/fundmng-icon-act.svg";
import { ReactComponent as ReportIcon } from "../../assets/icons/report-icon.svg";
import { ReactComponent as ReportIconHover } from "../../assets/icons/report-icon-h.svg";
import { ReactComponent as ReportIconActive } from "../../assets/icons/report-icon-act.svg";
import { ReactComponent as QuarterlyIcon } from "../../assets/icons/quarterly-icon.svg";
import { ReactComponent as QuarterlyIconHover } from "../../assets/icons/quarterly-icon-h.svg";
import { ReactComponent as QuarterlyIconActive } from "../../assets/icons/quarterly-icon-act.svg";
import { ReactComponent as ProfileIcon } from "../../assets/icons/student-profile-icon.svg";
import { ReactComponent as ProfileIconHover } from "../../assets/icons/student-profile-icon-h.svg";
import { ReactComponent as ProfileIconActive } from "../../assets/icons/student-profile-icon-act.svg";
import { ReactComponent as ExpenseIcon } from "../../assets/icons/student-expense-icon.svg";
import { ReactComponent as ExpenseIconHover } from "../../assets/icons/student-expense-icon-h.svg";
import { ReactComponent as ExpenseIconActive } from "../../assets/icons/student-expense-icon-act.svg";
import { ReactComponent as PaymentIcon } from "../../assets/icons/student-payment-icon.svg";
import { ReactComponent as PaymentIconHover } from "../../assets/icons/student-payment-icon-h.svg";
import { ReactComponent as PaymentIconActive } from "../../assets/icons/student-payment-icon-act.svg";

import { ReactComponent as AttendanceIcon } from "../../assets/icons/attendance-icon.svg";
import { ReactComponent as AttendanceIconHover } from "../../assets/icons/attendance-icon-h.svg";
import { ReactComponent as AttendanceIconActive } from "../../assets/icons/attendance-icon-act.svg";

interface SidebarProps {
  open: boolean;
  handleDrawerToggle?: () => void;
}

const drawerWidth = "19.375rem";

interface IconProps {
  isActive: boolean;
  isHovered: boolean;
  defaultIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  hoverIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  activeIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  openState: boolean;
}

const SidebarIcon: React.FC<IconProps> = ({
  isActive,
  isHovered,
  defaultIcon: DefaultIcon,
  hoverIcon: HoverIcon,
  activeIcon: ActiveIcon,
  openState,
}) => {
  let Icon = DefaultIcon;

  if (isActive) {
    Icon = openState ? HoverIcon : ActiveIcon;
  } else if (isHovered && openState) {
    Icon = HoverIcon;
  } else if (isHovered && !openState) {
    Icon = HoverIcon; // only show hoverIcon on collapsed menu hover if not active
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: 48,
        height: 48,
        borderRadius: "8px",
        backgroundColor: isActive
          ? openState
            ? "#E2EFFF"
            : "#0070D8"
          : "transparent",
      }}
    >
      <Icon />
    </Box>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ open }) => {
  const location = useLocation();
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const user = JSON.parse(localStorage.getItem("userData") || "{}")
  const userData: any = user?.modules;
  const id: number = user?.user_id;
  const isDesktop = useMediaQuery("(min-width:1025px)");
  const typeText: 'forecastReports' | 'allReports' = 'forecastReports';
  const menuItems = [
    {
      text: "Dashboard",
      path: "/",
      icons: {
        default: DashboardIcon,
        hover: DashboardIconHover,
        active: DashboardIconActive,
      },
      show: userData?.includes("admin-dashboard")
    },
    {
      text: "Application Management",
      path: "/application-management",
      icons: {
        default: ApplicationIcon,
        hover: ApplicationIconHover,
        active: ApplicationIconActive,
      },
      show: userData?.includes("application-management")
    },
    {
      text: "Scholarship Summary",
      path: "/scholarship-summary",
      icons: {
        default: ScholarshipIcon,
        hover: ScholarshipIconHover,
        active: ScholarshipIconActive,
      },
      show: userData?.includes("scholarship-summary")
    },
    {
      text: "Fund Management",
      path: "/fund-management",
      icons: {
        default: FundMngIcon,
        hover: FundMngIconHover,
        active: FundMngIconActive,
      },
       show: userData?.includes("fund-management")
    },
    {
      text: "Management Reports",
      path: `/management-reports/${typeText}`,
      icons: {
        default: ReportIcon,
        hover: ReportIconHover,
        active: ReportIconActive,
      },
       show: userData?.includes("management-reports")
    },
    {
      text: "Quarterly Evaluation",
      path: "/student-evaluation",
      icons: {
        default: QuarterlyIcon,
        hover: QuarterlyIconHover,
        active: QuarterlyIconActive,
      },
       show: userData?.includes("quarterly-evaluation")
    },
    {
      text: "My Profile",
      path: `/scholarship-summary/${id}`,
      icons: {
        default: ProfileIcon,
        hover: ProfileIconHover,
        active: ProfileIconActive,
      },
       show: userData?.includes(`student-profile`)
    },
    {
      text: "Payment History",
      path: `/fund-viewpayment/${user?.scholarship_id}`,
      icons: {
        default: PaymentIcon,
        hover: PaymentIconHover,
        active: PaymentIconActive,
      },
       show: userData?.includes(`student-payment-history`)
    },
    {
      text: "Expense History",
      path: `/fund-viewexpense/${user?.scholarship_id}`,
      icons: {
        default: ExpenseIcon,
        hover: ExpenseIconHover,
        active: ExpenseIconActive,
      },
       show: userData?.includes(`student-expense-summary`)
    },
    {
      text: "Attendance",
      path: `/attendance`,
      icons: {
        default: AttendanceIcon,
        hover: AttendanceIconHover,
        active: AttendanceIconActive,
      },
      //  show: userData.includes(`attendance`)
       show: true
    },
     
  ];

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? drawerWidth : isDesktop ? '4rem' : '4rem',
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: open ? drawerWidth : isDesktop ? '4rem' : '4rem',
          boxSizing: "border-box",
          transition: "width 0.3s ease",
          backgroundColor: "#fff",
          borderRight: "1px solid var(--mui-palette-divider)",
          overflowX: "hidden",
        },
      }}
    >
      <Toolbar className={styles.sidebarToolbar}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Link to={user.role === 'Student' ? `/scholarship-summary/${id}` : '/'} className={styles.logoLink}>
          {open ? (
            <img
              src={logo}
              alt="Logo"
              className={styles.logo}
              style={{ height: '3rem' }}
            />
          ) : (
            <img
              src={logosmall}
              alt="Logo"
              className={styles.logo}
              style={{ height: '3rem' }}
            />
          )}
          </Link>
        </Box>
      </Toolbar>

      <List className={styles.sidebarList}>
        {menuItems.map((item, index) => {
          const itemPathWithoutQuery = item.path.split("?")[0];
  const currentPath = location.pathname;

  // --- Smart Active Detection ---
  const isActive = (() => {
    // Exact match
    if (currentPath === itemPathWithoutQuery) return true;

    // Subroute or dynamic ID under the same parent
    if (
      currentPath.startsWith(itemPathWithoutQuery + "/") &&
      itemPathWithoutQuery !== "/dashboard"
    ) {
      return true;
    }

    // Query param case (/management-reports?type=xyz)
    if (
      item.path.includes("?") &&
      currentPath === itemPathWithoutQuery
    ) {
      return true;
    }

    return false;
  })();
          const isHovered = hoveredIndex === index;
          if(!item.show) return null
          return (
            <ListItem
              key={item.text}
              disablePadding
              sx={{ display: "block" }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <Tooltip title={!open ? item.text : ''} placement="right" arrow>
              <ListItemButton
                component={Link}
                to={item.path}
                className={`${styles.listItemButton} ${
                  isActive ? styles.active : ""
                }`}
                sx={{
                  minHeight: isDesktop ? 48 : 40,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 1 : "auto",
                    justifyContent: "center",
                    color: "#0070D8",
                  }}
                >
                  <SidebarIcon
                    isActive={isActive}
                    isHovered={isHovered}
                    defaultIcon={item.icons.default}
                    hoverIcon={item.icons.hover}
                    activeIcon={item.icons.active}
                    openState={open}
                  />
                </ListItemIcon>
                {open && (
                  <ListItemText primary={item.text} className="innerText" />
                )}
              </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
